import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ProjectsListTable, type ProjectsListColumn } from '../components/ProjectsListTable';
import { RegistrationReport, currentRoleBadge, type RegistrationSummaryEntry } from '../components/RegistrationReport';
import { useColumnOrder } from '../hooks/useColumnOrder';
import {
  adminApi,
  type CustomerRegistrationCounts,
  type CustomerRegistrationRow,
  type CustomerRegistrationSummary
} from '../services/api';

const SELF = 'self';

const DEFAULT_COLUMN_ORDER = [
  'fullName', 'email', 'activated', 'contractorCount', 'projectCount', 'registeredBy', 'registrarRole', 'registeredAt'
];

const activatedKey = (row: CustomerRegistrationRow) => (row.activated ? 'activated' : 'notActivated');

const counts = (value: CustomerRegistrationCounts): RegistrationSummaryEntry['counts'] =>
  [value.invited, value.activated, value.withProjects];

/**
 * Admin report (S8): which Etappio staff member invited which client, with
 * invited, activated and active clients per staff member. Clients invited by
 * a contractor or registered on their own are grouped together.
 */
export function AdminCustomerRegistrations() {
  const { t, i18n } = useTranslation();
  const [columnOrder, handleColumnReorder] = useColumnOrder('adminCustomerRegistrationsColumns', DEFAULT_COLUMN_ORDER);

  const locale = i18n.language === 'en' ? 'en-GB' : 'sv-SE';
  const formatDate = (value: string | null) =>
    value ? new Intl.DateTimeFormat(locale, { dateStyle: 'short' }).format(new Date(value)) : '';

  const summaryEntries = (summary: CustomerRegistrationSummary): RegistrationSummaryEntry[] => [
    ...summary.registrars.map((registrar) => ({
      key: registrar.userId,
      filter: registrar.userId,
      label: registrar.fullName || registrar.email || t('adminCompanyRegistrations.noName'),
      email: registrar.fullName ? registrar.email : null,
      badge: currentRoleBadge(t, registrar.currentRole),
      counts: counts(registrar)
    })),
    { key: SELF, filter: SELF, label: t('adminCustomerRegistrations.filter.self'), counts: counts(summary.self) }
  ];

  const registrarRoleLabel = (row: CustomerRegistrationRow) =>
    row.registrarRole ? t(`adminCompanyRegistrations.registrarRole.${row.registrarRole}`) : '';

  const registeredByLabel = (row: CustomerRegistrationRow) =>
    row.source === 'etappio_staff'
      ? row.registeredByName || row.registeredByEmail || t('adminCompanyRegistrations.noName')
      : t('adminCustomerRegistrations.list.self');

  const activatedBadge = (row: CustomerRegistrationRow) =>
    row.activated ? (
      <span className="badge bg-soft-success text-success">{t('adminCustomerRegistrations.list.activated')}</span>
    ) : (
      <span className="badge bg-soft-secondary text-secondary">{t('adminCustomerRegistrations.list.notActivated')}</span>
    );

  const activatedOptions = useMemo(() => [
    { value: 'activated', label: t('adminCustomerRegistrations.list.activated') },
    { value: 'notActivated', label: t('adminCustomerRegistrations.list.notActivated') }
  ], [t]);

  // Memoized: the table re-reports its rows whenever the columns change.
  const columns: ProjectsListColumn<CustomerRegistrationRow>[] = useMemo(() => [
    { field: 'fullName', label: t('adminCustomerRegistrations.list.columns.fullName'), render: row => <span className="fw-semibold text-break">{row.fullName}</span> },
    { field: 'email', label: t('adminCustomerRegistrations.list.columns.email'), sortValue: row => row.email ?? '', filterValue: row => row.email ?? '', render: row => <span className="text-break">{row.email}</span> },
    {
      field: 'activated',
      label: t('adminCustomerRegistrations.list.columns.activated'),
      filter: 'select',
      selectAllLabel: t('adminCompanyRegistrations.list.allStatuses'),
      selectOptions: activatedOptions,
      filterValue: activatedKey,
      sortValue: activatedKey,
      render: row => activatedBadge(row)
    },
    { field: 'contractorCount', label: t('adminCustomerRegistrations.list.columns.contractorCount'), filter: 'none', render: row => row.contractorCount },
    { field: 'projectCount', label: t('adminCustomerRegistrations.list.columns.projectCount'), filter: 'none', render: row => row.projectCount },
    {
      field: 'registeredBy',
      label: t('adminCompanyRegistrations.list.columns.registeredBy'),
      sortValue: registeredByLabel,
      filterValue: row => `${registeredByLabel(row)} ${row.registeredByEmail ?? ''}`,
      render: row => (
        <>
          {registeredByLabel(row)}
          {row.source === 'etappio_staff' && row.registeredByName && row.registeredByEmail && (
            <div className="small text-muted">{row.registeredByEmail}</div>
          )}
        </>
      )
    },
    {
      field: 'registrarRole',
      label: t('adminCompanyRegistrations.list.columns.registrarRole'),
      filter: 'select',
      selectAllLabel: t('adminCompanyRegistrations.list.allRoles'),
      selectOptions: [
        { value: 'admin', label: t('adminCompanyRegistrations.registrarRole.admin') },
        { value: 'sales', label: t('adminCompanyRegistrations.registrarRole.sales') }
      ],
      filterValue: row => row.registrarRole ?? '',
      sortValue: registrarRoleLabel,
      render: row => registrarRoleLabel(row)
    },
    {
      field: 'registeredAt',
      label: t('adminCompanyRegistrations.list.columns.registeredAt'),
      defaultSortDirection: 'desc',
      // Clients without a staff registration sort by when their account was created.
      sortValue: row => Date.parse(row.registeredAt ?? row.createdAt),
      filterValue: row => formatDate(row.registeredAt ?? row.createdAt),
      render: row => <span className="text-nowrap">{formatDate(row.registeredAt ?? row.createdAt)}</span>
    }
    // The renderers only depend on the language.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [t, locale, activatedOptions]);

  const renderMobileCard = (row: CustomerRegistrationRow) => (
    <div className="card mb-2 company-registrations__card" data-user-id={row.userId}>
      <div className="card-body py-2 px-3">
        <div className="admin-list-card__body">
          <div className="d-flex flex-wrap align-items-center gap-2 mb-1">
            <span className="fw-semibold text-break">{row.fullName}</span>
            {activatedBadge(row)}
          </div>
          {row.email && <div className="text-muted small text-break mb-2"><i className="feather-mail me-1" aria-hidden="true"></i>{row.email}</div>}
          <dl className="row small mb-0">
            <dt className="col-12 col-sm-4 fw-normal text-muted">{t('adminCustomerRegistrations.list.columns.contractorCount')}</dt>
            <dd className="col-12 col-sm-8">{row.contractorCount}</dd>
            <dt className="col-12 col-sm-4 fw-normal text-muted">{t('adminCustomerRegistrations.list.columns.projectCount')}</dt>
            <dd className="col-12 col-sm-8">{row.projectCount}</dd>
            <dt className="col-12 col-sm-4 fw-normal text-muted">{t('adminCompanyRegistrations.list.columns.registeredBy')}</dt>
            <dd className="col-12 col-sm-8 text-break">{registeredByLabel(row)}</dd>
            {row.registrarRole && (
              <>
                <dt className="col-12 col-sm-4 fw-normal text-muted">{t('adminCompanyRegistrations.list.columns.registrarRole')}</dt>
                <dd className="col-12 col-sm-8">{registrarRoleLabel(row)}</dd>
              </>
            )}
            <dt className="col-12 col-sm-4 fw-normal text-muted">{t('adminCompanyRegistrations.list.columns.registeredAt')}</dt>
            <dd className="col-12 col-sm-8 mb-0">{formatDate(row.registeredAt ?? row.createdAt)}</dd>
          </dl>
        </div>
      </div>
    </div>
  );

  return (
    <RegistrationReport<CustomerRegistrationSummary, CustomerRegistrationRow>
      i18nPrefix="adminCustomerRegistrations"
      breadcrumb={[
        { label: t('adminCompanyRegistrations.breadcrumb.home'), path: '/admin/home' },
        { label: t('appNav.customers'), path: '/admin/customers' },
        { label: t('adminCustomerRegistrations.title') }
      ]}
      filterId="customerRegistrationsFilter"
      countLabels={[
        t('adminCustomerRegistrations.summary.columns.invited'),
        t('adminCustomerRegistrations.summary.columns.activated'),
        t('adminCustomerRegistrations.summary.columns.withProjects')
      ]}
      loadSummary={adminApi.getCustomerRegistrationSummary}
      summaryEntries={summaryEntries}
      registrarCount={(summary) => summary.registrars.length}
      total={(summary) => summary.total}
      loadRows={adminApi.getCustomerRegistrations}
      exportRows={adminApi.exportCustomerRegistrations}
      renderList={(rows) => (
        <ProjectsListTable
          rows={rows}
          columns={columns}
          rowKey={row => row.userId}
          columnOrder={columnOrder}
          onColumnReorder={handleColumnReorder}
          reorderTitle={t('adminCompanyInvitations.dragToReorder')}
          initialSortField="registeredAt"
          initialSortDirection="desc"
          actionTitle=""
          emptyText={t('adminCustomerRegistrations.list.empty')}
          noResultsText={t('adminCustomerRegistrations.list.noResults')}
          rangeText={range => t('adminCustomerRegistrations.list.showingRange', range)}
          mobileFilter={{
            searchField: 'fullName',
            select: { field: 'activated', allLabel: t('adminCompanyRegistrations.list.allStatuses'), options: activatedOptions },
            sort: {
              label: t('projects.sort.label'),
              options: [
                { field: 'registeredAt', direction: 'desc', label: t('adminCompanyRegistrations.list.sort.newest') },
                { field: 'registeredAt', direction: 'asc', label: t('adminCompanyRegistrations.list.sort.oldest') },
                { field: 'fullName', direction: 'asc', label: t('adminCustomerRegistrations.list.sort.nameAZ') }
              ]
            }
          }}
          renderMobileCard={renderMobileCard}
          testIds={{ table: 'registration-table', cards: 'registration-cards' }}
        />
      )}
    />
  );
}
